import { useState, useRef, useEffect, useMemo, useCallback, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; 
import { ensureRedirectPath } from '../../util/redirectUtils';
import {
  getCloudFrontImageUrl,
  getCloudFrontOriginalImageUrl,
  getDirectS3StudioImageUrl
} from '../../util/cloudFrontUtils';
import awsConfig from '../../config/aws-config';
import fallbackImage from '../../public/notFount.png';
import rocketImage from "../../public/icons/rocket.png"

import { useFetchWishlists } from '../../hooks/wishlists/useFetchWishlists';
import { useDeleteWishListStudio } from '../../hooks/wishlists/useDeleteWishListStudio';

import { useSelector } from 'react-redux';
import { NavBarContext } from '../../App';
import { CarouselSearchPriorityContext } from '../../context/CarouselSearchPriorityContext';
import { trackStudioSelected } from '../../util/tracking';

import classes from './Carousel.module.css';
import { FavouriteModal } from './FavouriteModal/FavouriteModal';
import { notifyErrorToast } from '../../util/notifyActionError';
import FavouritePhone from './FavouriteModal/FavouritePhone';

import arrowLeft from '../../public/HomepageVectors/studioIcons/Property1.svg';
import arrowRight from '../../public/HomepageVectors/studioIcons/Property2.svg';
// import topStudio from "../../public/HomepageVectors/studioIcons/TopStudioIcon.svg";

/** How many slides before/after the current index get a real src (prefetch). Wider = faster when swiping quickly. */
const VISIBLE_NEIGHBOR_RANGE = 2;
const MAX_HARD_RETRIES_PER_IMAGE = 2;

const Carousel = ({ studio, renderedBy, isLoading, onImagesLoaded, linkFromSearch = false }) => {
  const { id: userID } = useSelector((state) => state?.auth?.user);
  const { 
    setShowSignIn, 
  } = useContext(NavBarContext);

  const searchPriorityCtx = useContext(CarouselSearchPriorityContext);

  const navigate = useNavigate();
  const location = useLocation();

  // Add wishlist hooks
  const { wishlistsData } = useFetchWishlists(userID);
  const { handleWishlistStudioDeletion } = useDeleteWishListStudio();

  // === STATE ===
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Simplified favorite functionality
  const [isFavorite, setIsFavorite] = useState(false);
  const [showFavouriteModal, setShowFavouriteModal] = useState(false);
  const [selectedWishlist, setSelectedWishlist] = useState(null);
  const [showSavedPopup, setShowSavedPopup] = useState(false);
  
  // Image loading state
  const [visibleIndices, setVisibleIndices] = useState({});
  const [loadedImages, setLoadedImages] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const [imageRetryStage, setImageRetryStage] = useState({});
  const [hardRetryNonceByIndex, setHardRetryNonceByIndex] = useState({});
  const [hardRetryCountByIndex, setHardRetryCountByIndex] = useState({});
  const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
  const stageRetryAttemptsRef = useRef({});
  const hardRetryTimersRef = useRef({});
  
  const touchStartTime = useRef(0);
  const popupTimerRef = useRef(null);

  const imageItems = useMemo(() => {
    if (!studio) return [];

    const rawImages = Array.isArray(studio.images) ? studio.images : [];
    const processedImages = Array.isArray(studio.processedImages) ? studio.processedImages : [];
    const originalImages = Array.isArray(studio.originalImages) ? studio.originalImages : [];

    return rawImages.map((rawImage, index) => ({
      raw: rawImage,
      preferredUrl: processedImages[index] || '',
      sourceKey: originalImages[index] || (typeof rawImage === 'string' ? rawImage : ''),
    }));
  }, [studio]);
  const totalImages = imageItems.length;

  // Add new drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragStartIndex, setDragStartIndex] = useState(0);

  // Add effect to check if studio is in any wishlist
  useEffect(() => {
    if (wishlistsData?.items && studio?.id) {
      const isInWishlist = wishlistsData.items.some(wishlist => 
        wishlist.wishListStudios?.items?.some(item => 
          item?.studioID === studio?.id
        )
      );
      setIsFavorite(isInWishlist);
    }
  }, [wishlistsData, studio?.id]);

  const handlePhoneSignInClick = () => {
    const from = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
    ensureRedirectPath(from);
  
    navigate('/anmelden', { state: { background: location, from, requiresAuth: true } });
  };

  const handleFavoriteClickPhone = async () => {
    if (!studio?.id) {
      return;
    }

    if (!userID) {
      handlePhoneSignInClick()
      return;
    }

    if (isFavorite) {
      const wishlistStudios = wishlistsData?.items?.flatMap(wishlist => 
        wishlist.wishListStudios?.items?.filter(item => 
          item?.studioID === studio?.id
        ) || []
      ) || [];

      try {
        for (const wishlistStudio of wishlistStudios) {
          await handleWishlistStudioDeletion(wishlistStudio?.id);
        }
        setIsFavorite(false);
        setSelectedWishlist(null);
        setShowSavedPopup(true);
        const timer = setTimeout(() => {
          setShowSavedPopup(false);
        }, 1400);
        return () => clearTimeout(timer);
      } catch (error) {
        console.error('Error removing from wishlists:', error);
        notifyErrorToast('Studio konnte nicht aus den Favoriten entfernt werden.', {
          title: 'Favoriten',
        });
      }
    } else {
      setShowFavouriteModal(true);
    }
  };

  const handleFavoriteClick = async () => {
    if (!studio?.id) {
      return;
    }

    if (!userID) {
      setShowSignIn(true);
      return;
    }

    if (isFavorite) {
      const wishlistStudios = wishlistsData?.items?.flatMap(wishlist => 
        wishlist.wishListStudios?.items?.filter(item => 
          item?.studioID === studio?.id
        ) || []
      ) || [];

      try {
        for (const wishlistStudio of wishlistStudios) {
          await handleWishlistStudioDeletion(wishlistStudio?.id);
        } 
        setIsFavorite(false);
        setSelectedWishlist(null);
        setShowSavedPopup(true);
        const timer = setTimeout(() => {
          setShowSavedPopup(false);
        }, 1400);
        return () => clearTimeout(timer);
      } catch (error) {
        console.error('Error removing from wishlists:', error);
        notifyErrorToast('Studio konnte nicht aus den Favoriten entfernt werden.', {
          title: 'Favoriten',
        });
      }
    } else {
      setShowFavouriteModal(true);
    }
  };

  const handleModalClose = () => {
    setShowFavouriteModal(false);
  };

  useEffect(() => {
    if (selectedWishlist) {
      setShowSavedPopup(true);
      setIsFavorite(true);
      const timer = setTimeout(() => {
        setShowSavedPopup(false);
      }, 1400);
      return () => clearTimeout(timer);
    }
  }, [selectedWishlist]);

  // Mark slides that should mount <img> and start loading (current ± VISIBLE_NEIGHBOR_RANGE)
  const updateVisibleImages = useCallback((index) => {
    if (!totalImages) return;

    const len = totalImages;
    const newVisibleIndices = {};
    for (let o = -VISIBLE_NEIGHBOR_RANGE; o <= VISIBLE_NEIGHBOR_RANGE; o++) {
      const i = index + o;
      if (i >= 0 && i < len) newVisibleIndices[i] = true;
    }

    setVisibleIndices((prev) => ({ ...prev, ...newVisibleIndices }));
  }, [totalImages]);

  // Memoized handler for image load success
  const handleImageLoad = useCallback((index, resolvedSrc) => {
    setLoadedImages(prev => ({...prev, [index]: true}));
    setImageRetryStage(prev => {
      if (prev[index] == null) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
    const retryPrefix = `${studio?.id || 'unknown'}-${index}-`;
    Object.keys(stageRetryAttemptsRef.current).forEach((key) => {
      if (key.startsWith(retryPrefix)) {
        delete stageRetryAttemptsRef.current[key];
      }
    });

    if (index === 0 && !isFirstImageLoaded) {
      setIsFirstImageLoaded(true);
      if (onImagesLoaded) onImagesLoaded();
    }
  }, [isFirstImageLoaded, onImagesLoaded, renderedBy, studio?.id]);

  // Memoized handler for image load failure
  const getImageFallbackUrl = useCallback((sourceKey, stage) => {
    if (!sourceKey || typeof sourceKey !== 'string') return '';

    if (stage === 1) {
      return getCloudFrontOriginalImageUrl(sourceKey);
    }
    if (stage === 2) {
      return getDirectS3StudioImageUrl(sourceKey);
    }
    return '';
  }, []);

  const appendNonceParam = useCallback((url, nonce) => {
    if (!url || !nonce || url.startsWith('data:')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}n=${nonce}`;
  }, []);

  const addCacheBust = useCallback((url, marker) => {
    if (!url) return url;
    if (url.startsWith('data:')) return url;
    const sanitizedUrl = url.replace(/([?&])retry=[^&]*(&)?/g, (match, prefix, trailingAmpersand) => {
      if (prefix === '?' && trailingAmpersand) return '?';
      if (prefix === '&' && trailingAmpersand) return '&';
      return '';
    }).replace(/[?&]$/, '');
    const separator = sanitizedUrl.includes('?') ? '&' : '?';
    return `${sanitizedUrl}${separator}retry=${Date.now()}-${marker}`;
  }, []);

  const handleImageError = useCallback((index, imageItem, attemptedUrl, element) => {
    const currentStage = imageRetryStage[index] || 0;
    const nextStage = currentStage + 1;
    const fallbackSourceKey = imageItem?.sourceKey || '';
    const fallbackUrl = getImageFallbackUrl(fallbackSourceKey, nextStage);
    const stageAttemptKey = `${studio?.id || 'unknown'}-${index}-${currentStage}`;
    const stageAttempts = stageRetryAttemptsRef.current[stageAttemptKey] || 0;

    // Only retry direct-s3 once; retrying CloudFront tokens caused ORB spikes.
    if (element && currentStage === 2 && !attemptedUrl?.startsWith('data:') && stageAttempts < 1) {
      const retryUrl = addCacheBust(attemptedUrl, `${index}-${currentStage}-${stageAttempts + 1}`);
      stageRetryAttemptsRef.current[stageAttemptKey] = stageAttempts + 1;
      element.src = retryUrl;
      return;
    }

    if (fallbackUrl && element && attemptedUrl !== fallbackUrl) {
      delete stageRetryAttemptsRef.current[stageAttemptKey];
      setImageRetryStage(prev => ({ ...prev, [index]: nextStage }));
      const nextRetryKey = `${studio?.id || 'unknown'}-${index}-${nextStage}`;
      stageRetryAttemptsRef.current[nextRetryKey] = 0;
      element.src = fallbackUrl;
      return;
    }

    if (failedImages[index]) return;
    const hardRetryCount = hardRetryCountByIndex[index] || 0;
    if (hardRetryCount < MAX_HARD_RETRIES_PER_IMAGE) {
      const retryDelayMs = (hardRetryCount + 1) * 1200;
      const existingTimer = hardRetryTimersRef.current[index];
      if (existingTimer) {
        clearTimeout(existingTimer);
      }
      hardRetryTimersRef.current[index] = setTimeout(() => {
        setHardRetryCountByIndex((prev) => ({ ...prev, [index]: hardRetryCount + 1 }));
        setHardRetryNonceByIndex((prev) => ({ ...prev, [index]: Date.now() }));
        setImageRetryStage((prev) => {
          if (prev[index] == null) return prev;
          const next = { ...prev };
          delete next[index];
          return next;
        });
        setLoadedImages((prev) => {
          if (prev[index] == null) return prev;
          const next = { ...prev };
          delete next[index];
          return next;
        });
        setFailedImages((prev) => {
          if (prev[index] == null) return prev;
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }, retryDelayMs);
      return;
    }

    setFailedImages(prev => ({...prev, [index]: true}));
    setLoadedImages(prev => ({...prev, [index]: true}));
    if (element) {
      element.onerror = null;
      element.src = fallbackImage;
    }

    if (index === 0 && !isFirstImageLoaded) {
      setIsFirstImageLoaded(true);
      if (onImagesLoaded) onImagesLoaded();
    }
  }, [addCacheBust, failedImages, getImageFallbackUrl, hardRetryCountByIndex, imageRetryStage, isFirstImageLoaded, onImagesLoaded, renderedBy, studio?.id]);

  // === NAVIGATION FUNCTIONS ===

  // Simplified navigation with preloading
  const prevSlide = useCallback(() => {
    if (!totalImages) return;
    
    const newIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    updateVisibleImages(newIndex);
  }, [currentIndex, totalImages, updateVisibleImages]);

  const nextSlide = useCallback(() => {
    if (!totalImages) return;
    
    const newIndex = currentIndex === totalImages - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    updateVisibleImages(newIndex);
  }, [currentIndex, totalImages, updateVisibleImages]);

  // === DRAG-BASED NAVIGATION FUNCTIONS ===
  
  const handleMouseDown = (e) => {
    if (!totalImages || totalImages <= 1) return;
    
    // Don't start drag if clicking on interactive elements
    if (e.target.closest(`.${classes.arrowLeftContainer}`) || 
        e.target.closest(`.${classes.arrowRightContainer}`) || 
        e.target.closest(`.${classes.favouriteContainer}`) ||
        e.target.closest(`.${classes.carouselDots}`)) {
      return;
    }

    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartIndex(currentIndex);
    setDragOffset(0);
    
    // Prevent text selection during drag
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !totalImages) return;
    
    const currentX = e.clientX;
    const diff = currentX - dragStartX;
    const containerWidth = e.currentTarget.offsetWidth;
    
    // Calculate drag offset as percentage of container width
    const offsetPercent = (diff / containerWidth) * 100;
    
    // Apply boundary constraints
    let constrainedOffset = offsetPercent;
    
    // Hard block drag beyond edges
    // At first image: block dragging right (positive offset)
    if (currentIndex === 0 && offsetPercent > 0) {
      constrainedOffset = 0;
    }
    
    // At last image: block dragging left (negative offset)
    if (currentIndex === totalImages - 1 && offsetPercent < 0) {
      constrainedOffset = 0;
    }
    
    setDragOffset(constrainedOffset);
  };

  const handleMouseUp = () => {
    if (!isDragging || !totalImages) return;
    
    const threshold = 5; // Changed from 50 to 5 - just 5% movement triggers change
    const dragDistance = Math.abs(dragOffset);
    
    if (dragDistance > threshold) {
      if (dragOffset > 0 && currentIndex > 0) {
        // Dragged right - go to previous image
        setCurrentIndex(currentIndex - 1);
      } else if (dragOffset < 0 && currentIndex < totalImages - 1) {
        // Dragged left - go to next image
        setCurrentIndex(currentIndex + 1);
      }
    }
    
    // Reset drag state
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  // Touch gesture with direction lock (for Safari & iOS)
  const containerRef = useRef(null);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const directionLockRef = useRef(null); // null | 'x' | 'y'
  const isTouchActiveRef = useRef(false);

  const handleTouchStart = useCallback((e) => {
    const imagesLength = studio?.images?.length || 0;
    if (imagesLength <= 1) return;

    const isOnInteractive =
      e.target.closest(`.${classes.arrowLeftContainer}`) ||
      e.target.closest(`.${classes.arrowRightContainer}`) ||
      e.target.closest(`.${classes.favouriteContainer}`) ||
      e.target.closest(`.${classes.carouselDots}`);

    if (isOnInteractive) return;

    const touch = e.touches[0];
    isTouchActiveRef.current = true;
    directionLockRef.current = null;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    setIsDragging(false);
    setDragStartX(touch.clientX);
    setDragStartIndex(currentIndex);
    setDragOffset(0);
    touchStartTime.current = Date.now();
  }, [totalImages, currentIndex]);

  const handleTouchMove = useCallback((e) => {
    const imagesLength = studio?.images?.length || 0;
    if (!isTouchActiveRef.current || imagesLength === 0) return;

    const isOnInteractive =
      e.target.closest(`.${classes.arrowLeftContainer}`) ||
      e.target.closest(`.${classes.arrowRightContainer}`) ||
      e.target.closest(`.${classes.favouriteContainer}`) ||
      e.target.closest(`.${classes.carouselDots}`);

    if (isOnInteractive) return;

    const touch = e.touches[0];
    const dx = touch.clientX - touchStartXRef.current;
    const dy = touch.clientY - touchStartYRef.current;

    // Decide the intent when movement is significant
    if (directionLockRef.current === null) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const intentThreshold = 8; // px
      if (absDx < intentThreshold && absDy < intentThreshold) return;
      if (absDx > absDy * 1.2) {
        directionLockRef.current = 'x';
        setIsDragging(true);
      } else if (absDy > absDx * 1.2) {
        directionLockRef.current = 'y';
        setIsDragging(false);
      } else {
        // ambiguous; do nothing yet
        return;
      }
    }

    if (directionLockRef.current === 'y') {
      // Let the page scroll naturally
      return;
    }

    // Horizontal gesture: prevent page scroll
    if (e.cancelable) e.preventDefault();

    const container = containerRef.current || e.currentTarget;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (dx / containerWidth) * 100;

    let constrainedOffset = offsetPercent;
    if (currentIndex === 0 && offsetPercent > 0) {
      constrainedOffset = 0;
    }
    if (currentIndex === imagesLength - 1 && offsetPercent < 0) {
      constrainedOffset = 0;
    }
    setDragOffset(constrainedOffset);
  }, [studio?.images?.length, currentIndex]);

  const handleTouchEnd = useCallback(() => {
    if (!isTouchActiveRef.current) return;

    if (directionLockRef.current === 'x') {
      const threshold = 5; // 5% movement triggers change
      const dragDistance = Math.abs(dragOffset);
      if (dragDistance > threshold) {
        if (dragOffset > 0 && currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        } else if (dragOffset < 0 && currentIndex < totalImages - 1) {
          setCurrentIndex(currentIndex + 1);
        }
      }
    }

    // Reset gesture state
    isTouchActiveRef.current = false;
    directionLockRef.current = null;
    setIsDragging(false);
    setDragOffset(0);
  }, [dragOffset, currentIndex, totalImages]);

  // Attach non-passive listeners to support preventDefault in Safari
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Reset states when studio changes
  useEffect(() => {
    if (studio?.id) {
      // Reset all image-related states when studio changes
      setVisibleIndices({});
      setLoadedImages({});
      setFailedImages({});
      setImageRetryStage({});
      setHardRetryNonceByIndex({});
      setHardRetryCountByIndex({});
      stageRetryAttemptsRef.current = {};
      Object.values(hardRetryTimersRef.current).forEach((timer) => clearTimeout(timer));
      hardRetryTimersRef.current = {};
      setIsFirstImageLoaded(false);
      setCurrentIndex(0);
    }
  }, [studio?.id]);

  useEffect(() => {
    return () => {
      Object.values(hardRetryTimersRef.current).forEach((timer) => clearTimeout(timer));
      hardRetryTimersRef.current = {};
    };
  }, []);

  // Initialize visible images when studio data is available
  useEffect(() => {
    if (!isLoading && totalImages > 0) {
      const initialVisibleIndices = {};
      const preloadCount = Math.min(
        totalImages,
        1 + VISIBLE_NEIGHBOR_RANGE * 2
      );
      for (let i = 0; i < preloadCount; i++) {
        initialVisibleIndices[i] = true;
      }
      setVisibleIndices(initialVisibleIndices);
    }
  }, [isLoading, renderedBy, totalImages]);

  // Update visible images when index changes
  useEffect(() => {
    updateVisibleImages(currentIndex);
  }, [currentIndex, updateVisibleImages]);

  // Add passive: false to touch event listeners to fix preventDefault error
  // useEffect(() => {
  //   const carouselElement = document.querySelector(`.${classes.carousel}`);
  //   if (!carouselElement) return;

  //   // Add touch event listeners with passive: false
  //   carouselElement.addEventListener('touchmove', handleTouchMove, { passive: false });
  //   carouselElement.addEventListener('touchstart', handleTouchStart, { passive: false });
  //   carouselElement.addEventListener('touchend', handleTouchEnd, { passive: false });

  //   return () => {
  //     carouselElement.removeEventListener('touchmove', handleTouchMove);
  //     carouselElement.removeEventListener('touchstart', handleTouchStart);
  //     carouselElement.removeEventListener('touchend', handleTouchEnd);
  //   };
  // }, [isDragging, currentIndex, studio?.images?.length]);

  useEffect(() => {
    const mobileMediaQuery = window.matchMedia('(max-width: 743px)');
    const desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
    
    const updateResponsiveState = () => {
      setIsMobile(mobileMediaQuery.matches);
      setIsDesktop(desktopMediaQuery.matches);
    };
    
    updateResponsiveState();
    
    mobileMediaQuery.addListener(updateResponsiveState);
    desktopMediaQuery.addListener(updateResponsiveState);
    
    return () => {
      mobileMediaQuery.removeListener(updateResponsiveState);
      desktopMediaQuery.removeListener(updateResponsiveState);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (popupTimerRef.current) {
        clearTimeout(popupTimerRef.current);
      }
    };
  }, []);

  // const handleClick = (event) => {
  //   if (renderedBy === 'StudioErstellen') {
  //     event.preventDefault();
  //   }
  // };

  // const handlePopupMouseEnter = () => {
  //   isHoveringRef.current = true;
  //   if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
  // };

  // const handlePopupMouseLeave = () => {
  //   isHoveringRef.current = false;
  //   if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
  //   popupTimerRef.current = setTimeout(() => {
  //     if (!isHoveringRef.current) setShowFavouriteModal(false);
  //   }, 2800);
  // };

  // Enhanced translateX calculation with drag offset and boundary constraints
  const translateX = useMemo(() => {
    const baseTranslate = -100 * currentIndex;
    if (isDragging) {
      return baseTranslate + dragOffset;
    }
    return baseTranslate;
  }, [currentIndex, isDragging, dragOffset]);
  
  const getImageUrl = useCallback((imageItem, index) => {
    if (!imageItem) {
      return fallbackImage;
    }
    
    if (!visibleIndices[index]) {
      return null;
    }

    // If we're in StudioErstellen mode, use the base64 URL directly
    if (renderedBy === 'StudioErstellen') {
      return imageItem.raw?.url || fallbackImage; // base64 string
    }

    // For search/list cards prefer already-processed URLs from hooks (same strategy as Studio page)
    if (imageItem.preferredUrl) {
      return appendNonceParam(imageItem.preferredUrl, hardRetryNonceByIndex[index]);
    }
    
    const sourceKey = imageItem.sourceKey;
    if (!sourceKey) {
      return fallbackImage;
    }

    // Otherwise, build CloudFront URL from source key
    try {
      const cleanKey = sourceKey.startsWith('public/') ? sourceKey : `public/${sourceKey}`;
      
      const cloudFrontUrl = getCloudFrontImageUrl(cleanKey, { 
        width: 450, 
        height: 400, 
        fit: 'cover' 
      });

      return appendNonceParam(cloudFrontUrl, hardRetryNonceByIndex[index]);
    } catch (error) {
      const cleanKey = sourceKey.startsWith('public/') ? sourceKey : `public/${sourceKey}`;
      const s3Url = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
      return appendNonceParam(s3Url, hardRetryNonceByIndex[index]);
    }
  }, [appendNonceParam, hardRetryNonceByIndex, visibleIndices, renderedBy]);

  /** HTML uses lowercase fetchpriority; React 18 warns on fetchPriority. Search grid: boost the carousel the user touched. */
  const getImgFetchPriority = useCallback(
    (index) => {
      if (!visibleIndices[index]) return 'low';
      const ctx = searchPriorityCtx;
      if (ctx && studio?.id) {
        if (ctx.activeStudioId === studio.id) {
          return index === currentIndex ? 'high' : 'auto';
        }
        if (ctx.activeStudioId != null) {
          return 'low';
        }
      }
      return index === currentIndex ? 'high' : 'low';
    },
    [searchPriorityCtx, studio?.id, visibleIndices, currentIndex]
  );

  const handleSearchPriorityPointer = useCallback(() => {
    if (!searchPriorityCtx?.setActiveStudioId || !studio?.id) return;
    searchPriorityCtx.setActiveStudioId(studio.id);
  }, [searchPriorityCtx, studio?.id]);

  const handleImageLoadWithDebug = useCallback((index, imageItem, event) => {
    const resolvedSrc = event.currentTarget?.currentSrc || event.currentTarget?.src;
    handleImageLoad(index, resolvedSrc);
  }, [handleImageLoad]);

  const renderCarouselItem = (imageItem, index) => {
    const itemKey = `${studio.id || 'preview'}-image-${index}`;
    const isVisible = visibleIndices[index] === true;
    const isLoaded = loadedImages[index] === true;
    const hasFailed = failedImages[index] === true;
    const imageUrl = getImageUrl(imageItem, index);
    const showLoadingBackdrop =
      isVisible && !isLoaded && !hasFailed && imageUrl != null;

    const imageElement = (
      <img
        src={imageUrl}
        alt={`Studio ${studio.StudioName || ''} - Image ${index + 1}`}
        fetchpriority={getImgFetchPriority(index)}
        onLoad={(e) =>
          handleImageLoadWithDebug(index, imageItem, e)
        }
        onError={(e) =>
          handleImageError(
            index,
            imageItem,
            e.currentTarget?.currentSrc || e.currentTarget?.src,
            e.currentTarget
          )
        }
        style={{
          objectFit: 'cover',
          width: '100%',
          height: '100%',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
        }}
      />
    );

    // If it's StudioErstellen, just return the image without a link
    if (renderedBy === 'StudioErstellen') {
      return (
        <div key={itemKey} className={classes.carouselItem}>
          {isVisible && (
            <>
              {showLoadingBackdrop && (
                <div className={classes.slideLoadingBackdrop} aria-hidden="true" />
              )}
              {imageElement}
            </>
          )}
        </div>
      );
    }

    const studioTo = linkFromSearch
      ? { pathname: `/studio/${studio.id}`, state: { from: 'search' } }
      : `/studio/${studio.id}`;
    const markOpenedFromSearch = () => {
      if (!linkFromSearch) return;
      trackStudioSelected({
        studio,
        source: 'search_results_image',
      });
      try {
        sessionStorage.setItem('stuStudioBackFrom', 'search');
      } catch (_) {
        /* ignore */
      }
    };

    // For other cases, wrap in a Link
    return (
      <div key={itemKey} className={classes.carouselItem}>
        {isVisible && (
          <>
            {showLoadingBackdrop && (
              <div className={classes.slideLoadingBackdrop} aria-hidden="true" />
            )}
            {isMobile ? (
              <div className={classes.carouselItemMobile}>
                <Link to={studioTo} onClick={markOpenedFromSearch}>
                  {imageElement}
                </Link>
              </div>
            ) : (
              <div className={classes.carouselItemDesktop}>
                <Link target="_blank" to={studioTo} onClick={markOpenedFromSearch}>
                  {imageElement}
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Update the saved popup content to show different messages
  const getSavedPopupContent = () => {
    if (!selectedWishlist) {
      return "Aus Wunschliste entfernt";
    }
    return `In ${selectedWishlist} gespeichert`;
  };

  const dotSizePx = 8;
  const dotGapPx = 5;
  const dotStepPx = dotSizePx + dotGapPx;
  const dotViewportSlots = useMemo(() => 5, []);
  const dotViewportWidthPx = useMemo(() => {
    if (dotViewportSlots <= 0) return 0;
    return dotViewportSlots * dotSizePx + (dotViewportSlots - 1) * dotGapPx;
  }, [dotViewportSlots]);
  const dotTrackOffsetSteps = useMemo(() => {
    if (totalImages <= 5) return 0;
    return Math.min(Math.max(currentIndex - 2, 0), totalImages - 5);
  }, [currentIndex, totalImages]);
  const dotTrackTranslateX = useMemo(
    () => -(dotTrackOffsetSteps * dotStepPx),
    [dotTrackOffsetSteps, dotStepPx]
  );

  // === RENDER ===
  return (
    <div
        className={`${classes.carousel} ${isLoading ? classes.loading : ''}`}
        ref={containerRef}
        onPointerDownCapture={handleSearchPriorityPointer}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: isLoading ? 'default' : isDragging ? 'grabbing' : 'grab' }} 
      >
      <div
        className={`${classes.carouselInner} ${isDragging ? classes.dragging : ''}`}
        style={{ transform: `translateX(${translateX}%)` }}
      >
        {isLoading || !studio || totalImages === 0 ? (
          <div className={`${classes.carouselItem} ${classes.skeleton}`}></div>
        ) : (
          imageItems.map((imageItem, index) => renderCarouselItem(imageItem, index))
        )}
      </div>
      
      {studio && totalImages > 1 && (
        <>
          {/* Top Studio Container */}
          {/* {renderedBy !== 'StudioErstellen' && (
            <div className={classes.topStudiosContainer}>
              <img src={topStudio} alt="Top Studio Icon" />
            </div>
          )} */}
          
          {/* Favorite Button */}
          {renderedBy !== 'MapComponent' && renderedBy !== 'StudioErstellen' && (
            <button onClick={handleFavoriteClick} className={classes.favouriteContainer}>
              <svg xmlns="http://www.w3.org/2000/svg" width="23" height="20" viewBox="0 0 23 20" fill="none">
                <path d="M1.26316 2.86401L1.2635 2.8635C2.27551 1.34011 4.05539 0.376707 5.80971 0.376707H5.81H5.81029H5.81058H5.81087H5.81116H5.81145H5.81174H5.81203H5.81232H5.81262H5.81291H5.8132H5.81349H5.81378H5.81407H5.81436H5.81466H5.81495H5.81524H5.81553H5.81583H5.81612H5.81641H5.8167H5.81699H5.81729H5.81758H5.81787H5.81817H5.81846H5.81875H5.81904H5.81934H5.81963H5.81992H5.82022H5.82051H5.8208H5.8211H5.82139H5.82169H5.82198H5.82227H5.82257H5.82286H5.82316H5.82345H5.82374H5.82404H5.82433H5.82463H5.82492H5.82522H5.82551H5.82581H5.8261H5.8264H5.82669H5.82699H5.82728H5.82758H5.82787H5.82817H5.82846H5.82876H5.82905H5.82935H5.82964H5.82994H5.83024H5.83053H5.83083H5.83112H5.83142H5.83171H5.83201H5.83231H5.8326H5.8329H5.8332H5.83349H5.83379H5.83408H5.83438H5.83468H5.83498H5.83527H5.83557H5.83587H5.83616H5.83646H5.83676H5.83705H5.83735H5.83765H5.83795H5.83824H5.83854H5.83884H5.83914H5.83943H5.83973H5.84003H5.84033H5.84062H5.84092H5.84122H5.84152H5.84181H5.84211H5.84241H5.84271H5.84301H5.84331H5.8436H5.8439H5.8442H5.8445H5.8448H5.8451H5.84539H5.84569H5.84599H5.84629H5.84659H5.84689H5.84719H5.84749H5.84778H5.84808H5.84838H5.84868H5.84898H5.84928H5.84958H5.84988H5.85018H5.85048H5.85078H5.85107H5.85137H5.85167H5.85197H5.85227H5.85257H5.85287H5.85317H5.85347H5.85377H5.85407H5.85437H5.85467H5.85497H5.85527H5.85557H5.85587H5.85617H5.85647H5.85677H5.85707H5.85737H5.85767H5.85797H5.85827H5.85857H5.85887H5.85917H5.85947H5.85977H5.86007H5.86037H5.86067H5.86097H5.86127H5.86157H5.86188H5.86218H5.86248H5.86278H5.86308H5.86338H5.86368H5.86398H5.86428H5.86458H5.86488H5.86518H5.86548H5.86578H5.86609H5.86639H5.86669H5.86699H5.86729H5.86759H5.86789H5.86819H5.86849H5.86879H5.8691H5.8694H5.8697H5.87H5.8703H5.8706H5.8709H5.8712H5.87151H5.87181H5.87211H5.87241H5.87271H5.87301H5.87331H5.87361H5.87392H5.87422H5.87452H5.87482H5.87512H5.87542H5.87572H5.87603H5.87633H5.87663H5.87693H5.87723H5.87753H5.87784H5.87814H5.87844H5.87874H5.87904H5.87934H5.87964H5.87995H5.88025H5.88055H5.88085H5.88115H5.88145H5.88176H5.88206H5.88236H5.88266H5.88296H5.88326H5.88357H5.88387H5.88417H5.88447H5.88477H5.88507H5.88538H5.88568H5.88598H5.88628H5.88658H5.88688H5.88719H5.88749H5.88779H5.88809H5.88839H5.88869H5.889H5.8893H5.8896H5.8899H5.8902H5.8905H5.89081H5.89111H5.89141H5.89171H5.89201H5.89231H5.89261H5.89292H5.89322H5.89352H5.89382H5.89412H5.89442H5.89473H5.89503H5.89533H5.89563H5.89593H5.89623H5.89653H5.89684H5.89714H5.89744H5.89774H5.89804H5.89834H5.89864H5.89895H5.89925H5.89955H5.89985H5.90015H5.90045H5.90075H5.90105H5.90136H5.90166H5.90196H5.90226H5.90256H5.90286H5.90316H5.90346H5.90377H5.90407H5.90437H5.90467H5.90497H5.90527H5.90557H5.90587H5.90617H5.90647H5.90677H5.90708H5.90738H5.90768H5.90798H5.90828H5.90858H5.90888H5.90918H5.90948H5.90978H5.91008H5.91038H5.91068H5.91098H5.91128H5.91159H5.91189H5.91219H5.91249H5.91279H5.91309H5.91339H5.91369H5.91399H5.91429H5.91459H5.91489H5.91519H5.91549H5.91579H5.91609H5.91639H5.91669H5.91699H5.91729H5.91759H5.91789H5.91819H5.91849H5.91879H5.91909H5.91939H5.91969H5.91998H5.92028H5.92058H5.92088H5.92118H5.92148H5.92178H5.92208H5.92238H5.92268H5.92298H5.92328H5.92358H5.92387H5.92417H5.92447H5.92477H5.92507H5.92537H5.92567H5.92597H5.92627H5.92656H5.92686H5.92716H5.92746H5.92776H5.92806H5.92835H5.92865H5.92895H5.92925H5.92955H5.92985H5.93014H5.93044H5.93074H5.93104H5.93133H5.93163H5.93193H5.93223H5.93253H5.93282H5.93312H5.93342H5.93372H5.93401H5.93431H5.93461H5.9349H5.9352H5.9355H5.9358H5.93609H5.93639H5.93669H5.93698H5.93728H5.93758H5.93787H5.93817H5.93847H5.93876H5.93906H5.93935H5.93965H5.93995H5.94024H5.94054H5.94084H5.94113H5.94143H5.94172H5.94202H5.94231H5.94261H5.94291H5.9432H5.9435H5.94379H5.94409H5.94438H5.94468H5.94497H5.94527H5.94556H5.94586H5.94615H5.94645H5.94674H5.94704H5.94733H5.94763H5.94792H5.94821H5.94851H5.9488H5.9491H5.94939H5.94968H5.94998H5.95027H5.95057H5.95086H5.95115H5.95145H5.95174H5.95203H5.95233H5.95262H5.95291H5.95321H5.9535H5.95379H5.95408H5.95438H5.95467H5.95496H5.95526H5.95555H5.95584H5.95613H5.95642H5.95647C7.23429 0.41688 8.49049 0.955255 9.69238 2.01102L9.69332 2.01184C10.0611 2.33274 10.3897 2.69 10.6943 3.10345L10.6945 3.10362L11.2093 3.80151L11.5142 4.21476L11.815 3.79857L12.3222 3.09686L12.3229 3.09593C12.6064 2.70122 12.9413 2.33716 13.3138 2.01167L13.315 2.0106C14.5176 0.950524 15.7752 0.415582 17.0551 0.376563C18.8668 0.330717 20.707 1.29747 21.7514 2.86401C22.7177 4.31349 22.7346 5.93235 22.4905 7.22522C22.2611 8.44015 21.8084 9.32696 21.7495 9.43995C20.9893 10.8262 20.2875 11.6685 19.8276 12.1841C18.6908 13.4499 16.1169 15.9991 11.5072 19.0517C6.57739 15.7921 4.19461 13.3647 3.20888 12.2031C2.55358 11.4287 1.92828 10.5422 1.2461 9.40402L1.24147 9.39629L1.23652 9.38887C1.24251 9.39785 1.2412 9.39536 1.23296 9.37973C1.22256 9.35998 1.20112 9.31928 1.1694 9.25427C1.11837 9.14967 1.04777 8.99646 0.96952 8.80292C0.812755 8.41517 0.626957 7.86963 0.505293 7.2317C0.261371 5.9527 0.280668 4.33775 1.26316 2.86401Z" 
                  fill={isFavorite ? "#CB1541" : "black"} 
                  fillOpacity={isFavorite ? "1" : "0.5"} 
                  stroke="white" 
                  strokeWidth="0.75"
                />
              </svg>
            </button>
          )}

          {renderedBy !== 'MapComponent' && renderedBy !== 'StudioErstellen' && (
            <button onClick={handleFavoriteClickPhone} className={`${classes.favouriteContainer} ${classes.favouriteContainerPhone}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="23" height="20" viewBox="0 0 23 20" fill="none">
                <path d="M1.26316 2.86401L1.2635 2.8635C2.27551 1.34011 4.05539 0.376707 5.80971 0.376707H5.81H5.81029H5.81058H5.81087H5.81116H5.81145H5.81174H5.81203H5.81232H5.81262H5.81291H5.8132H5.81349H5.81378H5.81407H5.81436H5.81466H5.81495H5.81524H5.81553H5.81583H5.81612H5.81641H5.8167H5.81699H5.81729H5.81758H5.81787H5.81817H5.81846H5.81875H5.81904H5.81934H5.81963H5.81992H5.82022H5.82051H5.8208H5.8211H5.82139H5.82169H5.82198H5.82227H5.82257H5.82286H5.82316H5.82345H5.82374H5.82404H5.82433H5.82463H5.82492H5.82522H5.82551H5.82581H5.8261H5.8264H5.82669H5.82699H5.82728H5.82758H5.82787H5.82817H5.82846H5.82876H5.82905H5.82935H5.82964H5.82994H5.83024H5.83053H5.83083H5.83112H5.83142H5.83171H5.83201H5.83231H5.8326H5.8329H5.8332H5.83349H5.83379H5.83408H5.83438H5.83468H5.83498H5.83527H5.83557H5.83587H5.83616H5.83646H5.83676H5.83705H5.83735H5.83765H5.83795H5.83824H5.83854H5.83884H5.83914H5.83943H5.83973H5.84003H5.84033H5.84062H5.84092H5.84122H5.84152H5.84181H5.84211H5.84241H5.84271H5.84301H5.84331H5.8436H5.8439H5.8442H5.8445H5.8448H5.8451H5.84539H5.84569H5.84599H5.84629H5.84659H5.84689H5.84719H5.84749H5.84778H5.84808H5.84838H5.84868H5.84898H5.84928H5.84958H5.84988H5.85018H5.85048H5.85078H5.85107H5.85137H5.85167H5.85197H5.85227H5.85257H5.85287H5.85317H5.85347H5.85377H5.85407H5.85437H5.85467H5.85497H5.85527H5.85557H5.85587H5.85617H5.85647H5.85677H5.85707H5.85737H5.85767H5.85797H5.85827H5.85857H5.85887H5.85917H5.85947H5.85977H5.86007H5.86037H5.86067H5.86097H5.86127H5.86157H5.86188H5.86218H5.86248H5.86278H5.86308H5.86338H5.86368H5.86398H5.86428H5.86458H5.86488H5.86518H5.86548H5.86578H5.86609H5.86639H5.86669H5.86699H5.86729H5.86759H5.86789H5.86819H5.86849H5.86879H5.8691H5.8694H5.8697H5.87H5.8703H5.8706H5.8709H5.8712H5.87151H5.87181H5.87211H5.87241H5.87271H5.87301H5.87331H5.87361H5.87392H5.87422H5.87452H5.87482H5.87512H5.87542H5.87572H5.87603H5.87633H5.87663H5.87693H5.87723H5.87753H5.87784H5.87814H5.87844H5.87874H5.87904H5.87934H5.87964H5.87995H5.88025H5.88055H5.88085H5.88115H5.88145H5.88176H5.88206H5.88236H5.88266H5.88296H5.88326H5.88357H5.88387H5.88417H5.88447H5.88477H5.88507H5.88538H5.88568H5.88598H5.88628H5.88658H5.88688H5.88719H5.88749H5.88779H5.88809H5.88839H5.88869H5.889H5.8893H5.8896H5.8899H5.8902H5.8905H5.89081H5.89111H5.89141H5.89171H5.89201H5.89231H5.89261H5.89292H5.89322H5.89352H5.89382H5.89412H5.89442H5.89473H5.89503H5.89533H5.89563H5.89593H5.89623H5.89653H5.89684H5.89714H5.89744H5.89774H5.89804H5.89834H5.89864H5.89895H5.89925H5.89955H5.89985H5.90015H5.90045H5.90075H5.90105H5.90136H5.90166H5.90196H5.90226H5.90256H5.90286H5.90316H5.90346H5.90377H5.90407H5.90437H5.90467H5.90497H5.90527H5.90557H5.90587H5.90617H5.90647H5.90677H5.90708H5.90738H5.90768H5.90798H5.90828H5.90858H5.90888H5.90918H5.90948H5.90978H5.91008H5.91038H5.91068H5.91098H5.91128H5.91159H5.91189H5.91219H5.91249H5.91279H5.91309H5.91339H5.91369H5.91399H5.91429H5.91459H5.91489H5.91519H5.91549H5.91579H5.91609H5.91639H5.91669H5.91699H5.91729H5.91759H5.91789H5.91819H5.91849H5.91879H5.91909H5.91939H5.91969H5.91998H5.92028H5.92058H5.92088H5.92118H5.92148H5.92178H5.92208H5.92238H5.92268H5.92298H5.92328H5.92358H5.92387H5.92417H5.92447H5.92477H5.92507H5.92537H5.92567H5.92597H5.92627H5.92656H5.92686H5.92716H5.92746H5.92776H5.92806H5.92835H5.92865H5.92895H5.92925H5.92955H5.92985H5.93014H5.93044H5.93074H5.93104H5.93133H5.93163H5.93193H5.93223H5.93253H5.93282H5.93312H5.93342H5.93372H5.93401H5.93431H5.93461H5.9349H5.9352H5.9355H5.9358H5.93609H5.93639H5.93669H5.93698H5.93728H5.93758H5.93787H5.93817H5.93847H5.93876H5.93906H5.93935H5.93965H5.93995H5.94024H5.94054H5.94084H5.94113H5.94143H5.94172H5.94202H5.94231H5.94261H5.94291H5.9432H5.9435H5.94379H5.94409H5.94438H5.94468H5.94497H5.94527H5.94556H5.94586H5.94615H5.94645H5.94674H5.94704H5.94733H5.94763H5.94792H5.94821H5.94851H5.9488H5.9491H5.94939H5.94968H5.94998H5.95027H5.95057H5.95086H5.95115H5.95145H5.95174H5.95203H5.95233H5.95262H5.95291H5.95321H5.9535H5.95379H5.95408H5.95438H5.95467H5.95496H5.95526H5.95555H5.95584H5.95613H5.95642H5.95647C7.23429 0.41688 8.49049 0.955255 9.69238 2.01102L9.69332 2.01184C10.0611 2.33274 10.3897 2.69 10.6943 3.10345L10.6945 3.10362L11.2093 3.80151L11.5142 4.21476L11.815 3.79857L12.3222 3.09686L12.3229 3.09593C12.6064 2.70122 12.9413 2.33716 13.3138 2.01167L13.315 2.0106C14.5176 0.950524 15.7752 0.415582 17.0551 0.376563C18.8668 0.330717 20.707 1.29747 21.7514 2.86401C22.7177 4.31349 22.7346 5.93235 22.4905 7.22522C22.2611 8.44015 21.8084 9.32696 21.7495 9.43995C20.9893 10.8262 20.2875 11.6685 19.8276 12.1841C18.6908 13.4499 16.1169 15.9991 11.5072 19.0517C6.57739 15.7921 4.19461 13.3647 3.20888 12.2031C2.55358 11.4287 1.92828 10.5422 1.2461 9.40402L1.24147 9.39629L1.23652 9.38887C1.24251 9.39785 1.2412 9.39536 1.23296 9.37973C1.22256 9.35998 1.20112 9.31928 1.1694 9.25427C1.11837 9.14967 1.04777 8.99646 0.96952 8.80292C0.812755 8.41517 0.626957 7.86963 0.505293 7.2317C0.261371 5.9527 0.280668 4.33775 1.26316 2.86401Z" 
                  fill={isFavorite ? "#CB1541" : "black"} 
                  fillOpacity={isFavorite ? "1" : "0.5"} 
                  stroke="white" 
                  strokeWidth="0.75"
                />
              </svg>
            </button>
          )}

          {/* {renderedBy === 'StudioErstellen' && ( */}
            <button className={`${classes.favouriteContainer} ${classes.rocketButton}` }>
                <img src={rocketImage} alt="rocket" />
                <span>stu original</span>
            </button>
          {/* )} */}
          
          {/* Navigation Arrows and Protective Divs */}
          {currentIndex > 0 ? (
            <div className={classes.arrowLeftContainer} onClick={prevSlide}>
              <img src={arrowLeft} alt="arrow left" />
            </div>
          ) : (
            <div 
              className={`${classes.arrowLeftContainer} ${classes.protectiveDiv}`}
              onClick={(e) => e.stopPropagation()}
            ></div>
          )}
          {currentIndex < totalImages - 1 ? (
            <div className={classes.arrowRightContainer} onClick={nextSlide}>
              <img src={arrowRight} alt="arrow right" />
            </div>
          ) : (
            <div 
              className={`${classes.arrowRightContainer} ${classes.protectiveDiv}`}
              onClick={(e) => e.stopPropagation()}
            ></div>
          )}
          
          {/* Navigation Dots (Airbnb-like clipped slider) */}
          <div className={classes.carouselDots}>
            <div
              className={classes.dotsViewport}
              style={{ width: `${dotViewportWidthPx}px` }}
            >
              <div
                className={classes.dotsTrack}
                style={{ transform: `translateX(${dotTrackTranslateX}px)` }}
              >
                {imageItems.map((_, dotIndex) => {
                  const isActive = dotIndex === currentIndex;
                  const isTerminalDot =
                    totalImages > 5 && (dotIndex === 0 || dotIndex === totalImages - 1);

                  return (
                    <span
                      key={dotIndex}
                      className={`${classes.dot} ${isActive ? classes.dotActive : ''} ${!isActive && isTerminalDot ? classes.dotTerminal : ''}`}
                      onClick={() => setCurrentIndex(dotIndex)}
                    ></span>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Keep only the saved popup */}
          <div 
            className={`${classes.favouritePopUpContainer} ${showSavedPopup ? classes.show : ''}`}
          >
            <div className={classes.favouritePopUpContent}>
              <div className={classes.clickContainer}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="14" viewBox="0 0 20 14" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M19.3695 1.38248L6.21971 13.89L6.21945 13.8897L6.21821 13.891L0.640625 8.19339L1.89618 6.96427L6.26245 11.4245L18.1586 0.109375L19.3695 1.38248Z" fill="white"/>
                </svg>
              </div>
              <div className={classes.favouriteTextSuccesfull}>
                <p className='semiboldSmall'>{getSavedPopupContent()}</p>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Keep the modal components */}
      {showFavouriteModal && !isMobile && (
        <FavouriteModal 
          studioId={studio.id}
          open={showFavouriteModal} 
          onBackdropClick={handleModalClose}
          setSelectedWishlist={setSelectedWishlist}
        />
      )}
      {showFavouriteModal && isMobile && (
          <FavouritePhone 
            showFavouriteModal={showFavouriteModal} 
            onClose={() => setShowFavouriteModal(false)}
            setSelectedWishlist={setSelectedWishlist}
            studioId={studio?.id}
            backdropClassName={classes.backdrop}
            backdropVisibleClassName={showFavouriteModal ? classes.backdropVisible : ''}
          />
      )}
    </div>
  );
};

export default Carousel;
